/**
 * Copyright (c) HashiCorp, Inc.
 */

import Component from '@glimmer/component';
import { service } from '@ember/service';
import { and, not, or } from 'ember-truth-helpers';
import {
  HdsPaginationCompact,
  HdsPaginationSizeSelector,
} from '@hashicorp/design-system-components/components';

import type RouterService from '@ember/routing/router-service';

export interface PaginationSignature {
  Args: {
    nextCursor?: string;
    prevCursor?: string;
    pageSizes?: number[];
    currentPageSize?: number;
    route?: string;
    model?: string;
    models?: string[] | unknown[];
    replace?: boolean;
    queryFunction?: (page: string) => {
      [key: string]: string | number;
    };
    onPageChange?: (page: string) => void;
    onPageSizeChange?: (size: number) => void;
  };

  Element: HTMLDivElement;
}

/**
 * `Cut::List::Pagination` provides a wrapper around the `Hds::Pagination::Compact` and `Hds::Pagination::SizeSelector` components.
 *
 * To use with route-based pagination
 * ```
 * <L.Pagination
 *  @pageSizes={{array 5 10 20 30}}
 *  @currentPageSize={{this.size}}
 *  @prevCursor={{this.prevCursor}}
 *  @nextCursor={{this.nextCursor}}
 * />
 * ```
 *
 * To use with functions
 * ```
 * <L.Pagination
 *  @pageSizes={{array 5 10 20 30}}
 *  @currentPageSize={{this.size}}
 *  @prevCursor={{this.prevCursor}}
 *  @nextCursor={{this.nextCursor}}
 *  @onPageChange={{this.updatePage}}
 *  @onPageSizeChange={{this.updatePageSize}}
 * />
 * ```
 *
 * @class PaginationComponent
 *
 * By default the pagination will use the router service to update the existing query params with the `page` and `pageSize` parameters
 * when interacting with the page selectors and the size selectors. You can pass in a `onPageChange` action to have it call a function insteand.
 * The same goes for the size selector, but the argument is `onPageSizeChange`.
 */
export default class Pagination extends Component<PaginationSignature> {
  @service declare readonly router: RouterService;

  get queryFunction(): (page: string) => {
    [key: string]: unknown;
  } {
    if (this.args.queryFunction) {
      return this.args.queryFunction;
    } else {
      const queryParams = this.router.currentRoute?.queryParams || {};

      return (page) =>
        Object.assign({}, queryParams, {
          previousPage: page === 'prev' ? this.args.prevCursor : undefined,
          nextPage: page === 'next' ? this.args.nextCursor : undefined,
        });
    }
  }

  onPageSizeChange = (size: number) => {
    if (this.args.onPageSizeChange) {
      this.args.onPageSizeChange(size);
    } else {
      const queryParams = this.router.currentRoute?.queryParams || {};
      const newParams = Object.assign({}, queryParams, {
        pageSize: size,
      });

      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: newParams,
      });
    }
  };

  <template>
    <div class="cut-list-pagination" ...attributes>
      {{#if @onPageChange}}
        <HdsPaginationCompact
          class="cut-list-pagination_page-selector"
          @showLabels={{true}}
          @isDisabledPrev={{not @prevCursor}}
          @isDisabledNext={{not @nextCursor}}
          @onPageChange={{@onPageChange}}
        />
      {{else}}
        {{! @glint-expect-error }}
        <HdsPaginationCompact
          class="cut-list-pagination_page-selector"
          @showLabels={{true}}
          @queryFunction={{this.queryFunction}}
          @route={{or @route this.router.currentRouteName}}
          @model={{@model}}
          @models={{@models}}
          @replace={{@replace}}
          @isDisabledPrev={{not @prevCursor}}
          @isDisabledNext={{not @nextCursor}}
        />
      {{/if}}

      {{#if (and @pageSizes @currentPageSize)}}
        <HdsPaginationSizeSelector
          class="cut-list-pagination_size-selector"
          {{! @glint-expect-error }}
          @pageSizes={{@pageSizes}}
          @label="Items per page"
          @selectedSize={{@currentPageSize}}
          @onChange={{this.onPageSizeChange}}
        />
      {{/if}}
    </div>
  </template>
}
